import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { SkeletonList } from '../../components/Skeleton';

/**
 * Phase 3.F — full alerts list for the customer side.
 *
 * Replaces the bell-only quick view with a real listing the manager
 * (and viewers, scoped to their own sites) can scan, filter, and
 * mark-as-read. The bell stays in the top header as a peek; this
 * page is where you go when you want to actually triage.
 *
 * Filters surface a single dropdown plus an "unread only" toggle.
 * Click an alert row → navigates to the gate's site map (the gate's
 * own slide-panel is reachable from there with one more click).
 */

const TYPE_LABEL = {
  long_open: 'שער פתוח זמן רב',
  long_open_warning: 'שער פתוח זמן רב',
  long_open_critical: 'שער פתוח זמן רב — קריטי',
  fault: 'תקלה בשער',
  offline: 'רכיב לא מחובר',
  unauthorized_open: 'פתיחה לא מורשית',
  cycle_warning: 'מתקרב לטיפול תקופתי',
  cycle_critical: 'הגיע ליעד טיפול תקופתי',
  close_failed: 'השער לא הצליח להיסגר',
};

const TYPE_GROUPS = [
  { key: 'all', label: 'הכל' },
  { key: 'unread', label: 'לא נקראו' },
  { key: 'long_open', label: 'פתיחה ארוכה' },
  { key: 'fault', label: 'תקלות' },
  { key: 'offline', label: 'לא מקוון' },
  { key: 'unauthorized_open', label: 'פתיחה לא מורשית' },
  { key: 'close_failed', label: 'סגירה כושלת' },
];

// Local-date helper for the date-range inputs. We compare timestamps
// in the browser's locale, not UTC, so "today" means what the user
// sees, not what the server logged.
function toISODateLocal(d) {
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
}

function CustomerAlertsList({ onAfterChange = () => {} }) {
  const [alerts, setAlerts] = useState(null);
  const [filter, setFilter] = useState('all');
  // Date-range filter — defaults to the last 30 days. The user can
  // widen it (set 'from' empty) or narrow it freely. Kept as strings
  // (YYYY-MM-DD) so <input type="date"> can be controlled directly.
  const initialFrom = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return toISODateLocal(d);
  }, []);
  const [dateFrom, setDateFrom] = useState(initialFrom);
  const [dateTo, setDateTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = () => {
    axios.get('/api/customer/alerts')
      .then(res => {
        setAlerts(res.data);
        setError(null);
      })
      .catch(err => setError(err.response?.data?.error || 'שגיאה בטעינה'));
  };
  useEffect(load, []);

  const markRead = async (alertId) => {
    try {
      await axios.post(`/api/customer/alerts/${alertId}/read`);
      setAlerts(prev =>
        prev ? prev.map(a => a.id === alertId ? { ...a, is_read: true } : a) : prev,
      );
      onAfterChange();
    } catch {
      // Silent on this one — the user will see the badge stay if it
      // failed; refreshing the page reconciles.
    }
  };

  const markAllRead = async () => {
    if (!alerts || !alerts.some(a => !a.is_read)) return;
    if (!window.confirm('לסמן את כל ההתראות הלא-נקראות כנקראו?')) return;
    setBusy(true);
    try {
      await axios.post('/api/customer/alerts/read-all');
      setAlerts(prev =>
        prev ? prev.map(a => ({ ...a, is_read: true })) : prev,
      );
      onAfterChange();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בעדכון');
    } finally {
      setBusy(false);
    }
  };

  const goToGate = (alertRow) => {
    if (!alertRow.is_read) markRead(alertRow.id);
    if (alertRow.site_id) {
      navigate(`/sites/${alertRow.site_id}`);
    }
  };

  if (alerts === null && !error) {
    return (
      <>
        <div className="section-header">
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, margin: 0 }}>התראות</h2>
        </div>
        <SkeletonList rows={5} />
      </>
    );
  }
  if (error) {
    return (
      <div style={{ color: '#b91c1c', padding: '1rem 0' }}>{error}</div>
    );
  }

  const unreadCount = alerts.filter(a => !a.is_read).length;

  // Date-range bounds → epoch ms once, reused inside the filter loop.
  // An empty 'from' or 'to' means "no bound on that side".
  const fromMs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
  const toMs = dateTo ? new Date(`${dateTo}T23:59:59.999`).getTime() : null;

  const passesType = (a) => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !a.is_read;
    if (filter === 'long_open') return (a.alert_type || '').startsWith('long_open');
    return a.alert_type === filter;
  };
  const passesDate = (a) => {
    if (!a.timestamp) return fromMs === null && toMs === null;
    const t = new Date(a.timestamp).getTime();
    if (fromMs !== null && t < fromMs) return false;
    if (toMs !== null && t > toMs) return false;
    return true;
  };
  const filtered = alerts.filter(a => passesType(a) && passesDate(a));
  const dateFilterActive = !!(dateFrom || dateTo);
  const clearDates = () => { setDateFrom(''); setDateTo(''); };

  return (
    <>
      <div className="section-header">
        <h2 style={{ fontSize: '1.1rem', fontWeight: 600, margin: 0 }}>
          התראות
          {unreadCount > 0 && (
            <span style={{
              marginRight: '0.6rem',
              fontSize: '0.85rem',
              color: '#b91c1c',
              fontWeight: 500,
            }}>
              ({unreadCount} לא נקראו)
            </span>
          )}
        </h2>
        {unreadCount > 0 && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={markAllRead}
            disabled={busy}
          >
            סמן הכל כנקרא
          </button>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.6rem' }}>
        {TYPE_GROUPS.map(g => (
          <button
            key={g.key}
            type="button"
            onClick={() => setFilter(g.key)}
            style={{
              padding: '0.3rem 0.85rem',
              background: filter === g.key ? '#1a1a2e' : '#fff',
              color: filter === g.key ? '#fff' : '#4a5568',
              border: '1px solid #e5e7eb',
              borderRadius: '999px',
              fontSize: '0.78rem',
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'Heebo, sans-serif',
            }}
          >
            {g.label}
          </button>
        ))}
      </div>

      {/* Date-range filter — sits below the type pills so the two
          filters compose visually. Defaults to the last 30 days; the
          user can widen by clearing 'from'. */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '0.5rem',
        marginBottom: '1rem',
        fontSize: '0.78rem',
        color: '#4a5568',
      }}>
        <span>טווח תאריכים:</span>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          מ-
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            style={{
              padding: '0.25rem 0.45rem',
              border: '1px solid #e5e7eb',
              borderRadius: 5,
              fontSize: '0.78rem',
              fontFamily: 'Heebo, sans-serif',
            }}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          עד
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            style={{
              padding: '0.25rem 0.45rem',
              border: '1px solid #e5e7eb',
              borderRadius: 5,
              fontSize: '0.78rem',
              fontFamily: 'Heebo, sans-serif',
            }}
          />
        </label>
        {dateFilterActive && (
          <button
            type="button"
            onClick={clearDates}
            style={{
              padding: '0.25rem 0.6rem',
              background: 'transparent',
              border: 'none',
              color: '#1a1a2e',
              textDecoration: 'underline',
              fontSize: '0.78rem',
              cursor: 'pointer',
              fontFamily: 'Heebo, sans-serif',
            }}
          >
            נקה תאריכים
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div style={{ color: '#9ca3af', padding: '2rem', textAlign: 'center' }}>
          {alerts.length === 0
            ? 'אין התראות. הכל תקין.'
            : 'אין התראות תואמות לסינון.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {filtered.map(a => {
            const when = a.timestamp
              ? new Date(a.timestamp).toLocaleString('he-IL', {
                  timeZone: 'Asia/Jerusalem',
                  day: '2-digit', month: '2-digit',
                  hour: '2-digit', minute: '2-digit',
                })
              : '';
            return (
              <div
                key={a.id}
                onClick={() => goToGate(a)}
                style={{
                  padding: '0.75rem 0.9rem',
                  background: a.is_read ? '#fff' : '#fef9c3',
                  border: `1px solid ${a.is_read ? '#e5e7eb' : '#fde68a'}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <span style={{
                    fontWeight: 600,
                    fontSize: '0.88rem',
                    color: '#1a1a2e',
                  }}>
                    {TYPE_LABEL[a.alert_type] || a.alert_type}
                  </span>
                  {!a.is_read && (
                    <span style={{
                      display: 'inline-block',
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: '#dc2626',
                    }} aria-label="לא נקרא" />
                  )}
                  <span style={{ marginInlineStart: 'auto', fontSize: '0.75rem', color: '#9ca3af' }}>
                    {when}
                  </span>
                </div>
                <div style={{ fontSize: '0.85rem', color: '#4a5568' }}>{a.message}</div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {a.site_name && <>אתר: {a.site_name}</>}
                  {a.gate_name && <> · שער: {a.gate_name}</>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

export default CustomerAlertsList;
